import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Users, Package, DollarSign, Globe, Rocket, GraduationCap, Award, Trophy,
  Activity, Megaphone, ShoppingBag, Truck, MapPinned, Calendar, ArrowRight,
  PlayCircle, MessageSquare, Headset, FileText, Sparkles, Settings as SettingsIcon,
  Building2, Store,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = {
  users: Users, package: Package, dollar: DollarSign, globe: Globe,
  click: Activity, check: Award, truck: Truck, shield: Trophy,
  store: Store, shop: ShoppingBag, headset: Headset, award: Award,
};

const TONES = {
  green:  { ring: "border-lifted-green/30", chip: "bg-lifted-green/15 text-lifted-green" },
  amber:  { ring: "border-amber-400/30",    chip: "bg-amber-500/15 text-amber-200" },
  purple: { ring: "border-purple-400/30",   chip: "bg-purple-500/15 text-purple-200" },
  sky:    { ring: "border-sky-400/30",      chip: "bg-sky-500/15 text-sky-200" },
};

function formatMetric(m) {
  if (m.value_money) {
    return `$${Number(m.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (typeof m.value === "number") return m.value.toLocaleString();
  return m.value ?? "—";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [roleData, setRoleData] = useState(null);
  const [activeRoleFilter, setActiveRoleFilter] = useState("all");
  const [err, setErr] = useState("");

  const isLoading = user === null;        // AuthProvider hasn't finished /auth/me
  const isLoggedOut = user === false;     // /auth/me returned 401

  useEffect(() => {
    if (!user || user === false) return;  // wait for hydration, skip if logged out
    let cancelled = false;
    (async () => {
      try {
        const [{ data: summary }, { data: roleAware }] = await Promise.all([
          api.get("/dashboard/master-summary"),
          api.get(`/dashboard/role-aware?role=${activeRoleFilter}`),
        ]);
        if (cancelled) return;
        setData(summary);
        setRoleData(roleAware);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status !== 401) {
          setErr(apiErrorMessage(e));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, activeRoleFilter]);

  if (isLoading)
    return <LoadingPanel label="Loading your Lifted dashboard…" />;
  if (isLoggedOut)
    return <LoadingPanel label="Session ended — redirecting…" />;

  // Phase 8.1 — customer-only accounts get the simplified Account/Orders
  // surface, not the full ecosystem dashboard. Multi-role customers (e.g.,
  // customer + affiliate) still see the full dashboard via the union check.
  const userRoles = (user?.roles) || [];
  const ECOSYSTEM_ROLES = new Set([
    "affiliate", "driver", "partner", "franchisee",
    "trainer", "teacher", "affiliate_trainer", "franchisee_trainer",
    "training_admin", "admin", "master_admin",
  ]);
  const isCustomerOnly = userRoles.length > 0 && !userRoles.some((r) => ECOSYSTEM_ROLES.has(r));
  if (isCustomerOnly) {
    return <Navigate to="/account" replace />;
  }

  if (err) return (
    <div className="glass-panel p-8 border border-rose-400/30">
      <div className="eyebrow text-rose-300 mb-2">Dashboard error</div>
      <div className="text-rose-200 text-sm">{err}</div>
    </div>
  );
  if (!data) return <LoadingPanel label="Syncing ecosystem data…" />;

  const isAdmin = (data.user.roles || []).some((r) => r === "admin" || r === "master_admin");
  const firstName = (() => {
    const raw = data.user.name || data.user.full_name || data.user.email || "Lifted";
    if (raw.includes("@")) return (raw.split("@")[0] || "Lifted").toUpperCase();
    return (raw.split(" ")[0] || "Lifted").toUpperCase();
  })();

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Phase 8.5A — TOP astronaut/dashboard MP4 header */}
      <TopDashboardHero firstName={firstName} />

      {/* Phase 8.6B — Welcome Back block removed per directive.
          Hero already greets the user dynamically: "HI {firstName}. READY TO LAUNCH." */}

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metric-cards">
        {data.metrics.map((m, idx) => {
          const Icon = ICONS[m.icon] || Users;
          const tone = TONES[m.tone] || TONES.green;
          return (
            <div
              key={`${m.key || "metric"}-${idx}`}
              className={`glass-panel p-5 border ${tone.ring}`}
              data-testid={`metric-${m.key}`}
            >
              <div className="flex items-start justify-between">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone.chip} border ${tone.ring}`}>
                  <Icon size={18} />
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-lifted-dim mt-3">
                {m.label}
              </div>
              <div className="font-anton text-3xl text-white mt-1">{formatMetric(m)}</div>
              {m.subtitle && (
                <div className="text-[10px] text-lifted-dim mt-1">{m.subtitle}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Phase 9.1 — Role-Aware Cards */}
      {roleData && (
        <RoleAwareSection
          data={roleData}
          activeFilter={activeRoleFilter}
          setFilter={setActiveRoleFilter}
        />
      )}

      {/* Phase 15 — Lifted Ecosystem WORLD video stays in the MIDDLE (do not move) */}
      <HeroBanner />

      {/* 3-column row: Performance + Progress + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PerformanceCard isAdmin={isAdmin} metrics={data.metrics} />
        <ProgressCard training={data.training} />
        <ActivityFeed activities={data.activities} />
      </div>

      {/* 3-column row: Ecosystem Map + Upcoming Events + Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <EcosystemMapCard map={data.ecosystem_map} />
        <UpcomingEventsCard />
        <TopPerformersCard performers={data.top_performers} />
      </div>

      {/* Quick access */}
      <QuickAccessRow roles={data.user.roles} />

      {/* Phase 6B — Training widget for current user */}
      <TrainingWidget />

      {/* Launchpad / Announcements / Brand */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LaunchpadCard />
        <AnnouncementsCard />
        <BrandCard />
      </div>
    </div>
  );
}

// ---------- sub-cards ----------

function PerformanceCard({ isAdmin, metrics }) {
  const ordersMetric = metrics.find((m) => m.key === "orders" || m.key === "assigned") || metrics[0];
  const revenue = metrics.find((m) => m.key === "revenue" || m.key === "commissions" || m.key === "earnings") || metrics[1];
  const clicks = metrics.find((m) => m.key === "clicks" || m.key === "members" || m.key === "available") || metrics[2];
  // Dedupe so React doesn't fire duplicate-key warnings if a fallback re-uses
  // the same metric. Slot order is preserved: orders → revenue → clicks.
  const slots = [ordersMetric, revenue, clicks].filter(Boolean);
  const seen = new Set();
  const uniqueSlots = slots.filter((m) => {
    if (seen.has(m.key)) return false;
    seen.add(m.key);
    return true;
  });
  return (
    <div className="glass-panel p-5 lg:col-span-1" data-testid="performance-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-anton text-xl text-white">Your Performance</div>
          <div className="text-[10px] text-lifted-dim">This Week</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        {uniqueSlots.map((m) => (
          <div key={m.key}>
            <div className="text-[10px] uppercase tracking-wider text-lifted-dim">{m.label}</div>
            <div className="font-anton text-2xl text-white mt-1">{formatMetric(m)}</div>
          </div>
        ))}
      </div>
      {/* Tiny inline sparkline */}
      <div className="mt-5 h-24 relative">
        <svg viewBox="0 0 200 60" className="w-full h-full">
          <polyline
            fill="none"
            stroke="rgb(184 255 103)"
            strokeWidth="2"
            points="0,50 30,40 60,32 90,28 120,20 150,25 180,12 200,8"
          />
          <polygon
            fill="rgb(184 255 103 / 0.15)"
            points="0,50 30,40 60,32 90,28 120,20 150,25 180,12 200,8 200,60 0,60"
          />
        </svg>
        <div className="absolute inset-x-0 bottom-0 flex justify-between text-[9px] text-lifted-dim pt-1">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <span key={d}>{d}</span>)}
        </div>
      </div>
    </div>
  );
}

function ProgressCard({ training }) {
  return (
    <div className="glass-panel p-5" data-testid="progress-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-anton text-xl text-white">Your Progress</div>
        </div>
        <Link to="/training" className="text-[10px] uppercase tracking-wider text-lifted-green">View all</Link>
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-white inline-flex items-center gap-2"><GraduationCap size={12}/> Training Modules</span>
            <span className="text-lifted-green">{training?.percent ?? 0}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded overflow-hidden">
            <div className="h-full bg-lifted-green" style={{ width: `${training?.percent ?? 0}%` }} />
          </div>
          <div className="text-[10px] text-lifted-dim mt-1">
            {training?.modules_completed ?? 0} / {training?.modules_total ?? 0} completed
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-white inline-flex items-center gap-2"><Trophy size={12}/> Certifications</span>
            <span className="text-lifted-green">{training?.certifications ?? 0}</span>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-white inline-flex items-center gap-2"><Award size={12}/> Achievements</span>
            <Link to="/achievements" className="text-lifted-green text-[10px]">View →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityFeed({ activities }) {
  return (
    <div className="glass-panel p-5" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="font-anton text-xl text-white">Ecosystem Activity</div>
        <span className="text-[10px] uppercase tracking-wider text-lifted-green flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-lifted-green animate-pulse"/> Live Feed
        </span>
      </div>
      {(activities || []).length === 0 ? (
        <div className="text-xs text-lifted-dim py-6 text-center">No ecosystem activity yet.</div>
      ) : (
        <ul className="space-y-3">
          {activities.slice(0, 5).map((a, i) => (
            <li key={i} className="flex items-center gap-3 text-xs" data-testid={`activity-${i}`}>
              <div className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-lifted-green">
                {a.kind === "announcement" ? <Megaphone size={12}/> :
                 a.kind?.startsWith("new_") ? <Users size={12}/> : <Activity size={12}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{a.title}</div>
                <div className="text-lifted-dim truncate">{a.subtitle}</div>
              </div>
              <div className="text-[10px] text-lifted-dim shrink-0">{relTime(a.at)}</div>
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/landing-pad"
        className="block text-center text-[11px] uppercase tracking-wider text-lifted-green border border-lifted-green/30 rounded mt-4 py-1.5 hover:bg-lifted-green/10"
      >
        View All Activity
      </Link>
    </div>
  );
}

function EcosystemMapCard({ map }) {
  return (
    <div className="glass-panel p-5" data-testid="ecosystem-map-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-anton text-xl text-white">Ecosystem Map</div>
          <div className="text-[10px] text-lifted-dim">See where we're growing together.</div>
        </div>
        <Link to="/ecosystem-map" className="text-[10px] uppercase tracking-wider text-lifted-green">View Map</Link>
      </div>
      <div className="h-36 rounded-lg border border-white/5 bg-[radial-gradient(circle_at_30%_40%,rgba(184,255,103,0.18),transparent_50%),radial-gradient(circle_at_70%_70%,rgba(99,102,241,0.18),transparent_50%)] mb-4" />
      <div className="grid grid-cols-4 gap-2 text-center text-xs">
        <div><div className="font-anton text-xl text-lifted-green">{map?.countries ?? 0}</div><div className="text-[9px] text-lifted-dim uppercase tracking-wider">Countries</div></div>
        <div><div className="font-anton text-xl text-lifted-green">{map?.cities ?? 0}</div><div className="text-[9px] text-lifted-dim uppercase tracking-wider">Cities</div></div>
        <div><div className="font-anton text-xl text-lifted-green">{(map?.members ?? 0).toLocaleString()}</div><div className="text-[9px] text-lifted-dim uppercase tracking-wider">Members</div></div>
        <div><div className="font-anton text-xl text-lifted-green">{map?.businesses ?? 0}</div><div className="text-[9px] text-lifted-dim uppercase tracking-wider">Businesses</div></div>
      </div>
    </div>
  );
}

function UpcomingEventsCard() {
  // Static seeded events — no fake data; only Lifted ecosystem internal calls
  const events = [
    { m: "JUN", d: "10", title: "Ecosystem Call",     subtitle: "Weekly Team Call",        time: "10:00 AM EST" },
    { m: "JUN", d: "14", title: "Training: Level Up", subtitle: "Advanced Strategies",     time: "2:00 PM EST" },
    { m: "JUN", d: "20", title: "Launchpad Live",     subtitle: "New Features Reveal",     time: "7:00 PM EST" },
  ];
  return (
    <div className="glass-panel p-5" data-testid="upcoming-events-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-anton text-xl text-white">Upcoming Events</div>
        <Link to="/events" className="text-[10px] uppercase tracking-wider text-lifted-green">View Calendar</Link>
      </div>
      <ul className="space-y-3">
        {events.map((e) => (
          <li key={e.d + e.title} className="flex items-start gap-3 text-xs">
            <div className="w-10 text-center shrink-0">
              <div className="text-[9px] uppercase tracking-wider text-lifted-dim">{e.m}</div>
              <div className="font-anton text-xl text-lifted-green leading-none">{e.d}</div>
            </div>
            <div className="flex-1">
              <div className="text-white">{e.title}</div>
              <div className="text-lifted-dim">{e.subtitle}</div>
            </div>
            <div className="text-[10px] text-lifted-dim">{e.time}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopPerformersCard({ performers }) {
  return (
    <div className="glass-panel p-5" data-testid="top-performers-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-anton text-xl text-white">Top Performers</div>
        <Link to="/leaderboard" className="text-[10px] uppercase tracking-wider text-lifted-green">This Month</Link>
      </div>
      {(performers || []).length === 0 ? (
        <div className="text-xs text-lifted-dim text-center py-6">No performance data yet.</div>
      ) : (
        <ul className="space-y-3">
          {performers.slice(0, 5).map((p, i) => (
            <li key={i} className="flex items-center gap-3 text-xs">
              <div className="w-5 text-center text-lifted-dim">{i + 1}</div>
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-lifted-green/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-lifted-green font-anton">
                {(p.name || "?")[0]}
              </div>
              <div className="flex-1 truncate">
                <div className="text-white truncate">{p.name}</div>
                <div className="text-[9px] text-lifted-dim uppercase tracking-wider">{p.tier}</div>
              </div>
              <div className="text-lifted-green font-anton">${Number(p.value || 0).toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/leaderboard"
        className="block text-center text-[11px] uppercase tracking-wider text-lifted-green border border-lifted-green/30 rounded mt-4 py-1.5 hover:bg-lifted-green/10"
      >
        View Leaderboard
      </Link>
    </div>
  );
}

function QuickAccessRow({ roles = [] }) {
  const items = [
    { to: "/training",            icon: GraduationCap, label: "Training Portal" },
    { to: "/affiliate/marketing", icon: Megaphone,     label: "Marketing Tools" },
    { to: "/same-day",            icon: Truck,         label: "Order Supplies" },
    { to: "/dashboard/contracts", icon: FileText,      label: "Contracts" },
    { to: "/admin/finances",      icon: DollarSign,    label: "Financials" },
    { to: "/landing-pad",         icon: Headset,       label: "Support Center" },
    { to: "/landing-pad",         icon: MessageSquare, label: "Community Forum" },
    { to: "/settings",            icon: SettingsIcon,  label: "Settings" },
  ];
  return (
    <div className="glass-panel p-5" data-testid="quick-access-row">
      <div className="font-anton text-xl text-white mb-3">Quick Access</div>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
        {items.map((it, idx) => {
          const Icon = it.icon;
          return (
            <Link
              key={`${it.label || "quick"}-${idx}`}
              to={it.to}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-white/5 hover:border-lifted-green/40 hover:bg-white/5 transition-colors"
              data-testid={`quick-${it.label.toLowerCase().replace(/\s+/g,"-")}`}
            >
              <Icon size={20} className="text-lifted-green" />
              <span className="text-[10px] text-center text-lifted-dim leading-tight">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function LaunchpadCard() {
  return (
    <div className="glass-panel p-5 relative overflow-hidden" data-testid="launchpad-card">
      <div className="font-anton text-xl text-white mb-2">Launchpad</div>
      <div className="text-xs text-white mb-1">Your journey starts here.</div>
      <p className="text-xs text-lifted-dim mb-4">
        Access tools, training, and resources to launch your success.
      </p>
      <Link
        to="/launchpad"
        className="inline-block bg-lifted-green text-black px-4 py-2 rounded text-[11px] font-bebas uppercase tracking-wider hover:bg-lifted-green/90"
      >
        Go to Launchpad
      </Link>
      <Rocket className="absolute right-4 bottom-4 text-lifted-green/30" size={64} />
    </div>
  );
}

function AnnouncementsCard() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/landing-pad/feed").then(({ data }) => setPosts(data.announcements || []))
       .catch(() => setPosts([]));
  }, []);
  return (
    <div className="glass-panel p-5" data-testid="announcements-card">
      <div className="flex items-center justify-between mb-3">
        <div className="font-anton text-xl text-white">Announcements</div>
        <Link to="/landing-pad" className="text-[10px] uppercase tracking-wider text-lifted-green">View All</Link>
      </div>
      {posts.length === 0 ? (
        <div className="text-xs text-lifted-dim py-6 text-center">No announcements yet.</div>
      ) : (
        <ul className="space-y-3 text-xs">
          {posts.slice(0, 3).map((p, idx) => (
            <li key={`${p.id || "post"}-${idx}`} className="flex items-start gap-3">
              <div className="h-7 w-7 rounded-lg bg-lifted-green/15 border border-lifted-green/30 flex items-center justify-center shrink-0">
                <Megaphone size={12} className="text-lifted-green"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{p.title}</div>
                <div className="text-lifted-dim truncate">{p.body?.slice(0, 60)}…</div>
              </div>
              <div className="text-[10px] text-lifted-dim shrink-0">{relTime(p.created_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BrandCard() {
  return (
    <div
      className="rounded-2xl border border-lifted-green/30 p-6 relative overflow-hidden bg-gradient-to-br from-black via-purple-900/30 to-lifted-green/10"
      data-testid="brand-card"
    >
      <div className="font-anton text-2xl text-white leading-tight">
        We don't compete.<br/>
        We collaborate.
      </div>
      <p className="text-xs text-lifted-dim mt-3">
        We don't just build businesses. We build a movement.
      </p>
      <div className="text-lifted-green font-anton text-lg mt-3">We are Lifted.</div>
      <Link
        to="/landing-pad"
        className="mt-4 inline-block border border-lifted-green/40 text-lifted-green px-4 py-2 rounded text-[11px] font-bebas uppercase tracking-wider hover:bg-lifted-green/10"
      >
        Share Your Success
      </Link>
      <Sparkles className="absolute right-4 top-4 text-lifted-green/40" size={32} />
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function LoadingPanel({ label }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 rounded-full border-2 border-lifted-green/30 border-t-lifted-green animate-spin" />
        <div className="font-bebas tracking-[0.3em] text-lifted-dim text-xs">{label}</div>
      </div>
    </div>
  );
}

// Phase 6B — compact training summary for the dashboard.
function TrainingWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/training/curriculum").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  if (!data || data === false) return null;
  const total = data.modules.length;
  const complete = data.modules.filter((m) => m.progress?.status === "complete").length;
  const inProgress = data.modules.filter((m) => m.progress?.status === "in_progress").length;
  const next = data.modules.find((m) => m.progress?.status !== "complete");
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  return (
    <div className="glass-panel p-5" data-testid="dashboard-training-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">YOUR TRAINING</div>
          <div className="font-anton text-xl text-white mt-1">
            {complete}/{total} modules <span className="text-lifted-green">complete</span>
          </div>
        </div>
        <Link to="/training" className="text-xs text-lifted-green underline" data-testid="dashboard-training-link">
          Open Training →
        </Link>
      </div>
      <div className="h-2 bg-black/40 rounded-full overflow-hidden">
        <div className="h-full bg-lifted-green" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
        <Stat label="Total" value={total} />
        <Stat label="In progress" value={inProgress} />
        <Stat label="Complete" value={complete} />
      </div>
      {next && (
        <Link to={`/m/${next.slug}`}
              className="mt-4 inline-flex items-center justify-between gap-2 w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 hover:border-lifted-green/40"
              data-testid="dashboard-training-next">
          <div>
            <div className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-green">NEXT UP · MODULE {next.order}</div>
            <div className="text-sm text-lifted-text truncate">{next.title}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-lifted-dim" />
        </Link>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-anton text-2xl text-white">{value}</div>
      <div className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroBanner — Phase 15 dashboard hero with autoplay MP4 background.
// Video lives on the right; text/CTAs sit on the left over a cosmic gradient
// so copy remains legible. Falls back to a still image if the <video> errors.
// ---------------------------------------------------------------------------
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/9bdsdg94_Lifted%20world.MP4";
const TOP_HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/imsbwbyj_gemini_generated_video_7BF50254.mp4";
const HERO_POSTER_URL = "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/odvbu9ai_IMG_1622.jpeg";

function HeroBanner({ firstName: _firstName }) {
  // Video stays mounted; on error we still keep the <video> tag in DOM so the
  // browser can retry on a later play() — and the poster acts as a graceful
  // fallback. We *also* render a CSS poster behind the video so the hero is
  // never visually blank while the 4 MB MP4 streams in.
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-lifted-green/20 bg-black shadow-[0_0_60px_-30px_rgba(184,255,103,0.55)]"
      data-testid="hero-banner"
    >
      {/* CSS poster — always present as a backstop */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url("${HERO_POSTER_URL}")` }}
        aria-hidden="true"
      />

      {/* Background video — autoplay, muted, loop, playsInline */}
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src={HERO_VIDEO_URL}
        poster={HERO_POSTER_URL}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        data-testid="hero-banner-video"
      />

      {/* Dark cosmic overlay — wider on the left so text is readable */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-black via-black/85 via-30% to-black/10"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60"
        aria-hidden="true"
      />
      {/* Neon green edge glow */}
      <div
        className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-lifted-green/10 rounded-2xl"
        aria-hidden="true"
      />

      <div className="relative px-6 py-10 md:px-12 md:py-14 md:pr-[44%] min-h-[300px] md:min-h-[380px] flex flex-col justify-center">
        <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.7rem]">
          WELCOME TO THE ALL-IN-ONE
        </div>
        <h2 className="font-anton text-4xl sm:text-5xl md:text-6xl uppercase text-white mt-2 leading-[0.92]">
          Lifted <span className="text-lifted-green">Ecosystem</span>
        </h2>
        <p className="text-white/70 mt-5 max-w-md text-sm md:text-base leading-relaxed">
          Education. Community. Opportunity. Supplies.<br/>
          Everything you need. All in one place.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/launchpad"
            className="bg-lifted-green text-black px-6 py-3 rounded-lg font-bebas uppercase tracking-[0.16em] text-sm hover:bg-lifted-green/90 inline-flex items-center gap-2 shadow-[0_0_24px_-6px_rgba(184,255,103,0.6)]"
            data-testid="hero-launchpad-cta"
          >
            Explore Launchpad <ArrowRight size={14} />
          </Link>
          <Link
            to="/training"
            className="border border-white/25 text-white px-6 py-3 rounded-lg font-bebas uppercase tracking-[0.16em] text-sm hover:bg-white/5 inline-flex items-center gap-2 backdrop-blur"
            data-testid="hero-watch-cta"
          >
            <PlayCircle size={14} /> Watch Overview
          </Link>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Phase 8.5A — TopDashboardHero
// ----------------------------------------------------------------------
// Premium brand header. Sits ABOVE the welcome line. Distinct from the
// middle <HeroBanner /> which keeps the Lifted Ecosystem world video.
function TopDashboardHero({ firstName }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_0_60px_-30px_rgba(184,255,103,0.4)]"
      data-testid="top-dashboard-hero"
    >
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src={TOP_HERO_VIDEO_URL}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        data-testid="top-dashboard-hero-video"
      />
      {/* Dark left → transparent right overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20"
        aria-hidden="true"
      />
      {/* Bottom fade for legibility */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40"
        aria-hidden="true"
      />
      {/* Phase 8.6A — Bright HTML overlay text that sits ABOVE any baked-in
          dark text in the MP4. Top-left placement to mirror the reference. */}
      <div className="absolute left-6 top-6 md:left-10 md:top-8 w-[78%] md:w-[55%] h-[120px] bg-black/30 blur-2xl rounded-2xl z-10 pointer-events-none" />
      <div className="absolute left-6 top-6 md:left-10 md:top-8 z-20 pointer-events-none">
        <div
          className="font-anton text-[40px] md:text-[64px] leading-[0.95] uppercase tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
          data-testid="top-hero-headline"
          style={{ textShadow: "0 0 18px rgba(184,255,103,0.35), 0 2px 8px rgba(0,0,0,0.9)" }}
        >
          LIFTED <span className="text-lifted-green">DASHBOARD</span>
        </div>
        <div
          className="mt-2 text-sm md:text-lg text-white/90 tracking-wide drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]"
          data-testid="top-hero-tagline"
        >
          Together we build. Together we rise.
        </div>
      </div>
      <div className="relative px-6 py-8 md:px-10 md:py-10 min-h-[240px] md:min-h-[300px] flex flex-col justify-end">
        <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.65rem]">
          LIFTED CORE · MISSION CONTROL
        </div>
        <div className="font-anton text-2xl sm:text-3xl md:text-4xl uppercase text-white mt-1">
          {firstName ? `Hi ${firstName}.` : "Hello, Lifted."}
          <span className="text-lifted-green"> Ready to launch.</span>
        </div>
      </div>
    </div>
  );
}



// ============================================================
// Phase 9.1 — Role-Aware Dashboard cards
// ============================================================
const ROLE_LABELS = {
  all: "All my roles",
  affiliate: "Affiliate",
  driver: "Driver",
  partner: "Partner",
  franchisee: "Franchisee",
  admin: "Admin",
};

function RoleAwareSection({ data, activeFilter, setFilter }) {
  const userRoles = (data.user?.roles || []).filter((r) =>
    ["affiliate", "driver", "partner", "franchisee"].includes(r),
  );
  const cards = data.cards || {};
  const isMulti = userRoles.length > 1;
  const isMaster = (data.user?.roles || []).includes("master_admin");
  const showSwitcher = isMulti || isMaster;
  const filterOptions = isMaster
    ? ["all", "affiliate", "driver", "partner", "franchisee"]
    : ["all", ...userRoles];

  return (
    <section className="space-y-3" data-testid="role-aware-section">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="eyebrow">Your Lifted dashboard</div>
          <h2 className="font-anton text-xl uppercase">
            {ROLE_LABELS[activeFilter] || "Your data"} · <span className="text-lifted-dim">{data.scope}</span>
          </h2>
        </div>
        {showSwitcher && (
          <div className="flex flex-wrap gap-1" data-testid="role-filter">
            {filterOptions.map((r) => (
              <button key={r} onClick={() => setFilter(r)}
                      className={`px-3 py-1.5 rounded-full border text-[0.65rem] font-bebas tracking-[0.18em] uppercase ${
                        activeFilter === r
                          ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                          : "border-white/10 text-lifted-dim hover:text-white"
                      }`}
                      data-testid={`role-filter-${r}`}>
                {ROLE_LABELS[r] || r}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="role-aware-kpis">
        {cards.total_ecosystem && (
          <KpiCard label={cards.total_ecosystem.label} value={cards.total_ecosystem.value}
                   sub={cards.total_ecosystem.by_role
                     ? Object.entries(cards.total_ecosystem.by_role).map(([k,v]) => `${k}:${v}`).join(" · ")
                     : null}
                   testid="role-kpi-ecosystem" />
        )}
        {cards.total_orders && (
          <KpiCard label={cards.total_orders.label} value={cards.total_orders.value} testid="role-kpi-orders" />
        )}
        {cards.total_revenue && (
          <KpiCard label={cards.total_revenue.label}
                   value={`$${((cards.total_revenue.value_cents || 0) / 100).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`}
                   testid="role-kpi-revenue" />
        )}
        {cards.active_locations && (
          <KpiCard label={cards.active_locations.label} value={cards.active_locations.value} testid="role-kpi-locations" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {cards.your_performance && (
          <div className="glass-panel p-4" data-testid="role-performance">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">Your performance</div>
            <ul className="text-xs space-y-1">
              {Object.entries(cards.your_performance).map(([k,v]) => (
                <li key={k} className="flex justify-between gap-2">
                  <span className="text-lifted-dim capitalize">{k.replace(/_/g," ")}</span>
                  <span className="text-lifted-text font-mono truncate max-w-[60%] text-right">
                    {typeof v === "object" ? JSON.stringify(v).slice(0,40) : String(v ?? "—")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {cards.training_status && (
          <div className="glass-panel p-4" data-testid="role-training">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">Training</div>
            <div className="font-anton text-2xl text-lifted-green">{cards.training_status.pct ?? "—"}%</div>
            <div className="text-[0.65rem] text-lifted-dim">{cards.training_status.completed ?? 0} of {cards.training_status.required ?? 0} slides · {cards.training_status.status}</div>
            <Link to="/training" className="text-[0.65rem] text-lifted-green hover:underline mt-2 inline-block">Open training →</Link>
            {cards.training_status.certification && (
              <div className="mt-2 text-[0.65rem] text-lifted-green">
                <Award className="inline h-3 w-3 mr-1" />
                {cards.training_status.certification.certification_id}
              </div>
            )}
          </div>
        )}
        {cards.role_tools && cards.role_tools.length > 0 && (
          <div className="glass-panel p-4" data-testid="role-tools">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">Your tools</div>
            <ul className="space-y-1">
              {cards.role_tools.map((t) => (
                <li key={t.href}>
                  <Link to={t.href} className="text-xs text-lifted-text hover:text-lifted-green flex items-center gap-1"
                        data-testid={`role-tool-${t.href.replace(/[^\w]/g,'-')}`}>
                    <ArrowRight className="h-3 w-3" /> {t.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{label}</div>
      <div className="font-anton text-3xl text-white mt-1">{value ?? "—"}</div>
      {sub && <div className="text-[0.6rem] text-lifted-dim mt-1">{sub}</div>}
    </div>
  );
}
